import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import WidgetWrapper from './WidgetWrapper';
import tagService from '../services/tagService';

// Helper function to format numbers with decimal and thousands separators
const formatNumber = (value, format) => {
  if (!format || value === null || value === undefined) return value;
  const numValue = Number(value);
  if (isNaN(numValue)) return value;

  let decimals = 2;
  let decimalSeparator = ',';
  let thousandsSeparator = '.';

  // Handle string format like "0.0", "0.00", "0"
  if (typeof format === 'string') {
    const match = format.match(/^0(\.0+)?$/);
    if (match) {
      decimals = match[1] ? match[1].length - 1 : 0;
    }
  } else if (typeof format === 'object') {
    decimals = format.decimals ?? 2;
    decimalSeparator = format.decimalSeparator || ',';
    thousandsSeparator = format.thousandsSeparator || '.';
  }

  const rounded = Number(numValue.toFixed(decimals));
  const [intPart, decPart] = rounded.toFixed(decimals).split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  return decPart ? `${formattedInt}${decimalSeparator}${decPart}` : formattedInt;
};

const SetValueInput = ({
  text = '',
  tagId = null,
  tagValue = null,
  value = null,
  unit = '',
  placeholder = 'Değer girin...',
  numberFormat = null, // { decimals, decimalSeparator, thousandsSeparator }
  isEditMode = false,
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  opacity,
  ...props
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [currentValue, setCurrentValue] = useState(tagValue);

  // Update current value when tagValue changes
  useEffect(() => {
    const newValue = value !== null && value !== undefined ? value : tagValue;
    setCurrentValue(newValue);
  }, [tagValue, value]);

  // Get display value
  const getDisplayValue = () => {
    if (currentValue !== null && currentValue !== undefined) {
      const formattedVal = numberFormat ? formatNumber(currentValue, numberFormat) : currentValue;
      return unit ? `${formattedVal} ${unit}` : String(formattedVal);
    }
    return text || placeholder;
  };

  const handleClick = (e) => {
    if (isEditMode || !tagId) return;
    e.stopPropagation();
    setInputValue(currentValue !== null && currentValue !== undefined ? String(currentValue) : '');
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setInputValue('');
  };

  const handleSave = async () => {
    if (!tagId || inputValue === '') {
      handleClose();
      return;
    }

    try {
      await tagService.updateTagValue(tagId, inputValue);
      setCurrentValue(inputValue);
      toast.success(`Değer gönderildi: ${inputValue}${unit ? ' ' + unit : ''}`, {
        position: 'top-right',
        autoClose: 3000
      });
      handleClose();
    } catch (error) {
      toast.error('Değer gönderilemedi!', {
        position: 'top-right',
        autoClose: 3000
      });
      handleClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleClose();
    }
  };

  // Context menu handler
  const handleContextMenu = (event) => {
    if (tagId) {
      event.preventDefault();
      event.stopPropagation();

      const displayName = (text && text !== 'Label') ? text : `Tag ${tagId}`;
      const tagInfoData = {
        tagId: tagId,
        tagName: displayName,
        tagName2: '',
        currentValue: currentValue,
        unit: unit || ''
      };
      window.dispatchEvent(new CustomEvent('openTagInfo', { detail: tagInfoData }));
    }
  };

  return (
    <>
      <WidgetWrapper
        backgroundColor={backgroundColor || '#ffffff'}
        foreColor={foreColor}
        borderWidth={borderWidth || 2}
        borderColor={borderColor || '#000'}
        borderRadius={borderRadius || 8}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fontFamily={fontFamily}
        opacity={opacity}
        justifyContent="center"
        padding="4px"
        onContextMenu={handleContextMenu}
        {...props}
      >
        <div
          onClick={handleClick}
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isEditMode || !tagId ? 'default' : 'pointer',
            color: foreColor || '#000',
            fontSize: fontSize ? `${fontSize}px` : '14px',
            fontWeight: fontWeight || 'normal',
            fontFamily: fontFamily || 'inherit',
            textAlign: 'center'
          }}
        >
          {getDisplayValue()}
        </div>
      </WidgetWrapper>

      {/* Set Value Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#fffde7',
            border: '3px solid #ffc107'
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#ffc107',
          color: '#000',
          fontWeight: 'bold',
          py: 1
        }}>
          Set Değeri Gir ( İsteğe Bağlı )
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <TextField
            autoFocus
            fullWidth
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            variant="outlined"
            InputProps={{
              sx: {
                fontSize: '18px'
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 4 }}>
          <Typography
            onClick={handleSave}
            sx={{
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '16px',
              color: '#000',
              '&:hover': { color: '#1976d2' }
            }}
          >
            Kaydet
          </Typography>
          <Typography
            onClick={handleClose}
            sx={{
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '16px',
              color: '#d32f2f',
              '&:hover': { color: '#b71c1c' }
            }}
          >
            İptal
          </Typography>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SetValueInput;
